import { useEffect, useState } from 'react';
import { FirestoreAddress } from '../types/address';
import { watchAddresses, listAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress } from '../services/addresses.service';
import { withErrorHandling } from '../lib/errors';

export function useAddresses(userId?: string) {
  const [addresses, setAddresses] = useState<(FirestoreAddress & { id: string })[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Guard: never start Firestore reads until userId is truthy
    if (!userId) { 
      // return early state, NO Firestore call
      setAddresses([]);
      setLoading(false);
      setError(null);
      return;
    }
    
    let unsub: (() => void) | null = null;
    let initialEmitted = false;
    
    setLoading(true);
    setError(null);
    
    // One-time fetch to populate immediately (in case listeners lag or rules block snapshot)
    withErrorHandling(
      () => listAddresses(),
      {
        ctx: { feature: 'addresses', action: 'listAddresses', userId },
        domainHint: 'firestore'
      }
    )
      .then(items => { 
        if (!initialEmitted) {
          setAddresses(items);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!initialEmitted) {
          // Removed debug log to prevent sensitive data exposure
          setError(err.message || 'Failed to load addresses');
          setLoading(false);
        }
      });

    // Live listener
    try {
      unsub = watchAddresses((items) => {
        initialEmitted = true;
        setAddresses(items);
        setLoading(false);
        setError(null);
      });
    } catch (err) {
      // Removed debug log to prevent sensitive data exposure
      setError('Failed to set up address listener');
      setLoading(false);
    }

    return () => { 
      if (unsub) unsub(); 
    };
  }, [userId]);

  const create = async (data: Omit<FirestoreAddress, "DateTime">) => {
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    return withErrorHandling(
      () => createAddress(data),
      {
        ctx: { feature: 'addresses', action: 'createAddress', userId },
        domainHint: 'firestore'
      }
    );
  };

  const update = async (id: string, data: Partial<FirestoreAddress>) => {
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    return withErrorHandling(
      () => updateAddress(id, data),
      {
        ctx: { feature: 'addresses', action: 'updateAddress', userId, extra: { addressId: id } },
        domainHint: 'firestore'
      }
    );
  };

  const remove = async (id: string) => {
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    return withErrorHandling(
      () => deleteAddress(id),
      {
        ctx: { feature: 'addresses', action: 'deleteAddress', userId, extra: { addressId: id } },
        domainHint: 'firestore'
      }
    );
  };

  const setDefault = async (id: string) => {
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    return withErrorHandling(
      () => setDefaultAddress(id),
      {
        ctx: { feature: 'addresses', action: 'setDefaultAddress', userId, extra: { addressId: id } },
        domainHint: 'firestore'
      }
    );
  };

  return { addresses, loading, error, create, update, remove, setDefault };
}




